import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchItemHistory, compareChildren } from '../api/orcanosClient';
import {
  parseHistory,
  extractTags,
  diffBetween,
  diffHtml,
  CURRENT,
  tagId,
} from '../utils/history';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import Spinner from '../ui/Spinner';

const escapeHtml = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const RUN_CLASS = { eq: '', del: 'compare-old', ins: 'compare-new' };

// A rich-text field rendered the way the reader sees it — words, line breaks
// and images — as one track-changes run: removed words struck red, added words
// green, an image outlined red or green when it was removed or added.
//
// Built as a string and injected through sanitizeHtml rather than as React
// elements: sanitizeHtml is what resolves Orcanos-relative image URLs and adds
// the covarisFTP access token, and the window-level broken-image fallback
// replaces <img> nodes in place, which React must not own.
function htmlDiffMarkup(from, to) {
  let html = '';
  let open = null;
  const close = () => { if (open) { html += '</span>'; open = null; } };

  diffHtml(from, to).forEach(({ op, tok }, idx) => {
    if (tok.t === 'br') { close(); html += '<br>'; return; }
    if (op !== 'eq' && open !== op) { close(); html += `<span class="${RUN_CLASS[op]}">`; open = op; }
    if (op === 'eq') close();
    if (idx > 0 && !html.endsWith('<br>')) html += ' ';
    if (tok.t === 'word') html += escapeHtml(tok.v);
    else {
      const src = escapeHtml(tok.src);
      html += `<a href="${src}"><img src="${src}" alt="${escapeHtml(tok.alt || tok.key)}"></a>`;
    }
  });
  close();
  return sanitizeHtml(html);
}

// The field-by-field table — the same rendering for the part itself and for
// every child, so a child's diff reads exactly like its parent's.
function FieldsTable({ fields }) {
  return (
    <div className="compare-table-wrap">
      <table className="bom-grid compare-table">
        <thead>
          <tr>
            <th className="compare-col-field">Field</th>
            <th>Change</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.field}>
              <td className="compare-col-field">
                {f.field}
                {f.touches > 1 && (
                  <span
                    className="compare-touches"
                    title={`Changed ${f.touches} times in this range — showing the first old value and the last new value`}
                  >
                    ×{f.touches}
                  </span>
                )}
              </td>
              <td className="compare-col-change">
                {f.html ? (
                  <div
                    className="compare-html"
                    dangerouslySetInnerHTML={{ __html: htmlDiffMarkup(f.from, f.to) }}
                  />
                ) : (
                  <>
                    {!f.added && <del className="compare-old">{f.fromText}</del>}
                    {!f.added && !f.removed && <span className="compare-sep">→</span>}
                    {!f.removed && <ins className="compare-new">{f.toText}</ins>}
                  </>
                )}
                {f.added && <span className="compare-tag-added">added</span>}
                {f.removed && <span className="compare-tag-removed">cleared</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// The id / type / keys used to read ONE item's history. Revisions and their
// tags belong to the PART, not to an instance of it, so a PI is read through
// the master part it instantiates (`cs21Int` / `originalId` are the PRT-side
// id — see _normalizeRow).
function historyTarget(r) {
  const isPI = r.type === 'PI';
  const id = isPI ? (r.cs21Int || r.originalId) : r.itemId;
  return {
    id,
    type: isPI ? 'PRT' : (r.type || 'PRT'),
    // Keys used to tell this item's own tags from a linked item's. For a PI the
    // row's own Key belongs to the instance, so only the part id is meaningful.
    keys: isPI ? [id] : [r.userPrefix, r.objName, r.itemId],
  };
}

const STATUS_ORDER = { changed: 0, missing: 1, error: 2, same: 3 };
// Changed first (most fields first), then missing, failed, unchanged.
const sortResults = (rs) =>
  rs.sort(
    (x, y) =>
      STATUS_ORDER[x.status] - STATUS_ORDER[y.status] ||
      (y.diff?.fields.length || 0) - (x.diff?.fields.length || 0) ||
      x.label.localeCompare(y.label)
  );

// One streamed server line → the row the list renders. The server sends the
// change-log SLICE between the two tags; the diff is made here with the same
// `diffBetween` the parent uses, so a child reads exactly like its parent.
function toResult(item, msg) {
  if (msg.status === 'missing') {
    return { ...item, status: 'missing', missing: msg.missing, tags: (msg.tags || []).map((tag) => ({ tag })) };
  }
  if (msg.status === 'error') {
    return { ...item, status: 'error', message: msg.message || 'Failed to load history.' };
  }
  const d = diffBetween(parseHistory(msg.history), msg.from_no, msg.to_no ?? CURRENT.changeNo);
  return {
    ...item,
    status: d.fields.length || d.descriptionChanged ? 'changed' : 'same',
    diff: d,
    cached: !!msg.cached,
  };
}

const STATUS_LABEL = {
  changed: 'Changed',
  same: 'No changes',
  missing: 'Tag not found',
  error: 'Failed',
};

// Compare two revisions (tags) of a part — or a tag against its current state.
//
// A "tag" is a frozen snapshot Orcanos takes when an item is signed (or when a
// user creates one by hand). The API has no tag endpoint: they surface only as
// specially-worded comments in the item's History, so we read the whole change
// log once and derive everything from it. See src/utils/history.js.
//
// Because the diff is computed from the log itself, "Current" costs no extra
// call — the last NewValue recorded for a field IS its current value.
//
// `getChildren` (optional) returns this row's DIRECT children, which powers
// "Show children": the same two tags, looked up BY NAME on each child. A tag
// number is per item, so the only thing that ties a child's snapshot to its
// parent's is the tag label — a child that was never tagged with it is
// reported as such, never silently diffed against something else.
// See Critical Note #76.

export default function CompareModal({ row, onClose, onAuthExpired, getChildren }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromId, setFromId] = useState(null);
  const [toId, setToId] = useState(CURRENT.id);

  const target = useMemo(() => historyTarget(row), [row]);
  const itemLabel = row.userPrefix || row.objName || `Item ${target.id}`;

  useEffect(() => {
    let cancelled = false;
    // RecordCount is a top-N cap, not pagination — ask for far more than any
    // real change log so nothing is silently truncated.
    fetchItemHistory({
      itemId: target.id,
      itemType: target.type,
      recordCount: 1000,
    })
      .then((data) => {
        if (cancelled) return;
        setEvents(parseHistory(data));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoading(false);
        if (err.httpCode === 401) { onAuthExpired?.(); onClose(); }
        else setError(err.message || 'Failed to load history.');
      });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tags = useMemo(
    () => (events ? extractTags(events, target.keys) : []),
    [events] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Default the older endpoint to the newest tag, compared against Current.
  useEffect(() => {
    if (fromId === null && tags.length > 0) setFromId(tagId(tags[0]));
  }, [tags, fromId]);

  const options = useMemo(
    () => [
      { id: CURRENT.id, label: CURRENT.label, sub: 'as it stands now', changeNo: CURRENT.changeNo, tag: null },
      ...tags.map((t) => ({
        id: tagId(t),
        label: `Tag ${t.tag}${t.approved ? ' (approved)' : ''}`,
        sub: [t.when, t.who].filter(Boolean).join(' · '),
        changeNo: t.changeNo,
        tag: t.tag,
      })),
    ],
    [tags]
  );

  const optionOf = (id) => options.find((o) => o.id === id);
  const changeNoOf = (id) => optionOf(id)?.changeNo ?? null;
  const labelOf = (id) => optionOf(id)?.label ?? '—';

  const fromNo = changeNoOf(fromId);
  const toNo = changeNoOf(toId);
  const samePoint = fromId != null && fromId === toId;

  const diff = useMemo(() => {
    if (!events || fromNo == null || toNo == null || samePoint) return null;
    return diffBetween(events, fromNo, toNo);
  }, [events, fromNo, toNo, samePoint]);

  // Which endpoint is older decides the reading direction of the labels.
  const olderId = fromNo != null && toNo != null && fromNo > toNo ? toId : fromId;
  const newerId = olderId === fromId ? toId : fromId;

  // ─── Children ──────────────────────────────────────────────────────────
  // `null` until asked for. A run is identified by a token so that closing the
  // panel, changing an endpoint or unmounting abandons it mid-flight.
  const [children, setChildren] = useState(null);
  const [filter, setFilter] = useState('changed');
  const [query, setQuery] = useState('');
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const runRef = useRef(0);
  const abortRef = useRef(null);

  // Abandon a run: stale results are ignored, and the stream is cut so the
  // server stops calling Orcanos too.
  const cancelRun = () => {
    runRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
  };

  // The endpoints moved → whatever the children were compared at is stale.
  useEffect(() => {
    cancelRun();
    setChildren(null);
    setOpenKeys(new Set());
  }, [fromId, toId]);

  useEffect(() => cancelRun, []);

  async function showChildren() {
    const token = ++runRef.current;
    const live = () => runRef.current === token;
    const fromTag = optionOf(olderId)?.tag ?? null; // null → Current
    const toTag = optionOf(newerId)?.tag ?? null;

    setChildren({ phase: 'listing', done: 0, total: 0, results: [] });
    let rows;
    try {
      rows = await getChildren?.();
    } catch (err) {
      if (live()) setChildren({ phase: 'done', done: 0, total: 0, results: [], error: err.message });
      return;
    }
    if (!live()) return;
    if (!rows) { setChildren(null); return; } // the caller already reported it
    if (rows.length === 0) {
      setChildren({ phase: 'done', done: 0, total: 0, results: [], empty: true });
      return;
    }

    // The same part can sit under a parent more than once. Its history is the
    // same history — read it once and say how many times it appears.
    const byId = new Map();
    for (const r of rows) {
      const t = historyTarget(r);
      if (!t.id) continue;
      const seen = byId.get(String(t.id));
      if (seen) { seen.count += 1; continue; }
      byId.set(String(t.id), {
        key: String(t.id),
        target: t,
        label: r.userPrefix || r.objName || `Item ${t.id}`,
        sub: r.userPrefix && r.objName ? r.objName : '',
        count: 1,
        status: 'pending',
      });
    }
    const items = [...byId.values()];
    setChildren({ phase: 'comparing', done: 0, total: items.length, cached: 0, results: [] });

    // ONE request: the server reads every child's history (ten at a time, and
    // not at all for a tag pair it has already cached) and streams each answer
    // back as it lands, so the list fills in while the rest are still running.
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const results = [];
    try {
      await compareChildren({
        items: items.map((i) => ({
          id: String(i.target.id),
          type: i.target.type,
          keys: i.target.keys.filter(Boolean).map(String),
        })),
        fromTag,
        toTag,
        signal: ctrl.signal,
        onLine: (msg) => {
          if (!live()) return;
          if (msg.type === 'start') {
            setChildren((c) => (c ? { ...c, cached: msg.cached } : c));
            return;
          }
          if (msg.type !== 'item') return;
          const item = byId.get(String(msg.id));
          if (!item) return;
          results.push(toResult(item, msg));
          const snapshot = sortResults([...results]);
          setChildren((c) => (c ? { ...c, done: snapshot.length, results: snapshot } : c));
        },
      });
    } catch (err) {
      if (err.name === 'AbortError' || !live()) return;
      if (err.httpCode === 401) { onAuthExpired?.(); onClose(); return; }
      // Keep what already arrived — a failure halfway loses nothing that finished.
      setChildren((c) => ({
        ...(c || {}), phase: 'done', total: results.length, results: sortResults([...results]),
        error: err.message || 'Compare failed.',
      }));
      return;
    }

    if (!live()) return;
    abortRef.current = null;
    setChildren((c) => ({ ...(c || {}), phase: 'done', done: results.length, total: items.length,
      results: sortResults([...results]) }));
  }

  function stopChildren() {
    cancelRun();
    setChildren(null);
  }

  const counts = useMemo(() => {
    const c = { changed: 0, same: 0, missing: 0, error: 0 };
    for (const r of children?.results || []) c[r.status] += 1;
    return c;
  }, [children]);

  const shownChildren = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (children?.results || []).filter(
      (r) =>
        (filter === 'all' || r.status === filter) &&
        (!q || `${r.label} ${r.sub}`.toLowerCase().includes(q))
    );
  }, [children, filter, query]);

  // A filter that now hides everything is a blank panel with no explanation —
  // fall back to "all" the moment the chosen bucket empties out.
  useEffect(() => {
    if (children?.phase === 'done' && filter !== 'all' && counts[filter] === 0) setFilter('all');
  }, [children, filter, counts]);

  const toggleChild = (key) =>
    setOpenKeys((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const allOpen = shownChildren.length > 0 && shownChildren.every((r) => openKeys.has(r.key));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal compare-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Compare Revisions — {itemLabel}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body compare-modal-body">
          {loading && (
            <div className="compare-state">
              <Spinner size={20} />
              <p>Loading change history…</p>
            </div>
          )}

          {!loading && error && (
            <div className="compare-state"><p className="compare-error">{error}</p></div>
          )}

          {!loading && !error && tags.length === 0 && (
            <div className="compare-state">
              <p>
                No revision tags were found for this item. Orcanos creates a tag
                when an item is signed, so an item that has never been through a
                signature has nothing to compare.
              </p>
            </div>
          )}

          {!loading && !error && tags.length > 0 && (
            <>
              <div className="compare-pickers">
                <label className="compare-picker">
                  <span className="compare-picker-label">From</span>
                  <select value={fromId ?? ''} onChange={(e) => setFromId(e.target.value)}>
                    {options.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}{o.sub ? ` — ${o.sub}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="compare-arrow">→</span>
                <label className="compare-picker">
                  <span className="compare-picker-label">To</span>
                  <select value={toId} onChange={(e) => setToId(e.target.value)}>
                    {options.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}{o.sub ? ` — ${o.sub}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {samePoint && (
                <div className="compare-state">
                  <p>Pick two different points to compare.</p>
                </div>
              )}

              {diff && (
                <>
                  <div className="compare-summary">
                    {diff.fields.length === 0
                      ? `No field changes between ${labelOf(olderId)} and ${labelOf(newerId)}.`
                      : `${diff.fields.length} field${diff.fields.length === 1 ? '' : 's'} changed between ${labelOf(olderId)} and ${labelOf(newerId)} · ${diff.events.length} change event${diff.events.length === 1 ? '' : 's'}.`}
                  </div>

                  {diff.fields.length > 0 && <FieldsTable fields={diff.fields} />}

                  {/* The API records THAT the rich-text description changed but
                      carries no before/after text for it, so we can only flag it. */}
                  {diff.descriptionChanged && (
                    <div className="compare-note">
                      The Description was edited in this range. Orcanos does not
                      return the before and after text, so the change is not shown
                      here — open the item in Orcanos to read it.
                    </div>
                  )}

                  {diff.events.length > 0 && (
                    <details className="compare-events">
                      <summary>Change events in this range ({diff.events.length})</summary>
                      <table className="bom-grid compare-events-table">
                        <thead>
                          <tr>
                            <th>Change #</th>
                            <th>When</th>
                            <th>Who</th>
                            <th>Comment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diff.events.map((ev) => (
                            <tr key={ev.changeNo}>
                              <td>{ev.changeNo}</td>
                              <td>{ev.when}</td>
                              <td>{ev.who}</td>
                              <td>{ev.comment}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  )}

                  {getChildren && (
                    <div className="compare-children">
                      <div className="compare-children-bar">
                        {!children ? (
                          <>
                            <button className="btn-secondary" onClick={showChildren}>
                              Show children
                            </button>
                            <span className="compare-children-hint">
                              Compares {labelOf(olderId)} → {labelOf(newerId)} on every direct
                              child, matching each child's tag by name.
                            </span>
                          </>
                        ) : (
                          <>
                            <button className="btn-secondary" onClick={stopChildren}>
                              {children.phase === 'done' ? 'Hide children' : 'Stop'}
                            </button>
                            {children.phase !== 'done' && (
                              <span className="compare-children-hint">
                                <Spinner size={14} />{' '}
                                {children.phase === 'listing'
                                  ? 'Reading children…'
                                  : `Comparing children… ${children.done} / ${children.total}${children.cached ? ` (${children.cached} from cache)` : ''}`}
                              </span>
                            )}
                            {children.phase === 'done' && (
                              <span className="compare-children-hint">
                                {children.total} child{children.total === 1 ? '' : 'ren'} ·{' '}
                                {counts.changed} changed
                                {counts.missing ? ` · ${counts.missing} without the tag` : ''}
                                {counts.error ? ` · ${counts.error} failed` : ''}
                              </span>
                            )}
                          </>
                        )}
                      </div>

                      {children?.phase === 'comparing' && children.total > 0 && (
                        <div className="compare-progress">
                          <div
                            className="compare-progress-fill"
                            style={{ width: `${Math.round((children.done / children.total) * 100)}%` }}
                          />
                        </div>
                      )}

                      {children?.error && (
                        <div className="compare-state"><p className="compare-error">{children.error}</p></div>
                      )}

                      {children?.empty && (
                        <div className="compare-state"><p>This item has no children.</p></div>
                      )}

                      {children?.results?.length > 0 && (
                        <>
                          <div className="compare-children-tools">
                            <div className="compare-chips">
                              {['changed', 'same', 'missing', 'error', 'all'].map((k) => {
                                const n = k === 'all' ? children.results.length : counts[k];
                                if (k !== 'all' && n === 0) return null;
                                return (
                                  <button
                                    key={k}
                                    className={`compare-chip${filter === k ? ' is-active' : ''}`}
                                    onClick={() => setFilter(k)}
                                  >
                                    {k === 'all' ? 'All' : STATUS_LABEL[k]} ({n})
                                  </button>
                                );
                              })}
                            </div>
                            <input
                              className="compare-children-search"
                              type="search"
                              placeholder="Filter by key or name…"
                              value={query}
                              onChange={(e) => setQuery(e.target.value)}
                            />
                            <button
                              className="compare-linkish"
                              onClick={() =>
                                setOpenKeys(
                                  allOpen ? new Set() : new Set(shownChildren.map((r) => r.key))
                                )
                              }
                            >
                              {allOpen ? 'Collapse all' : 'Expand all'}
                            </button>
                          </div>

                          {shownChildren.length === 0 ? (
                            <div className="compare-state">
                              <p>{children.phase === 'done' ? 'No child matches this filter.' : 'None yet — still comparing…'}</p>
                            </div>
                          ) : (
                            <ul className="compare-child-list">
                              {shownChildren.map((c) => {
                                const open = openKeys.has(c.key);
                                const expandable = c.status === 'changed';
                                return (
                                  <li key={c.key} className="compare-child">
                                    <button
                                      className="compare-child-head"
                                      onClick={() => expandable && toggleChild(c.key)}
                                      aria-expanded={expandable ? open : undefined}
                                    >
                                      <span className="compare-child-caret">
                                        {expandable ? (open ? '▾' : '▸') : ''}
                                      </span>
                                      <span className="compare-child-name">
                                        {c.label}
                                        {c.count > 1 && (
                                          <span className="compare-touches" title="Appears this many times under the parent">
                                            ×{c.count}
                                          </span>
                                        )}
                                        {c.sub && <span className="compare-child-sub">{c.sub}</span>}
                                      </span>
                                      <span className={`compare-child-status is-${c.status}`}>
                                        {c.status === 'changed'
                                          ? `${c.diff.fields.length} field${c.diff.fields.length === 1 ? '' : 's'} changed`
                                          : c.status === 'missing'
                                            ? `No tag ${c.missing.join(' / ')}`
                                            : c.status === 'error'
                                              ? 'Failed'
                                              : 'No changes'}
                                      </span>
                                    </button>

                                    {c.status === 'missing' && (
                                      <div className="compare-child-note">
                                        This child was never tagged {c.missing.join(' or ')}.
                                        {c.tags.length > 0
                                          ? ` It has: ${c.tags.slice(0, 6).map((t) => t.tag).join(', ')}${c.tags.length > 6 ? '…' : ''}.`
                                          : ' It has no tags at all.'}
                                      </div>
                                    )}
                                    {c.status === 'error' && (
                                      <div className="compare-child-note compare-error">{c.message}</div>
                                    )}

                                    {open && c.status === 'changed' && (
                                      <div className="compare-child-body">
                                        {c.diff.fields.length > 0 && <FieldsTable fields={c.diff.fields} />}
                                        {c.diff.descriptionChanged && (
                                          <div className="compare-note">
                                            The Description was edited in this range. Orcanos does not
                                            return the before and after text, so the change is not shown
                                            here — open the item in Orcanos to read it.
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
